import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { sendLovableEmail } from "@lovable.dev/email-js";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

const RECIPIENT_EMAIL = "info@calvarius.com";
const SENDER_DOMAIN = "notify.calvarius.com";
const FROM_EMAIL = "Calvarius Kontaktformular <noreply@notify.calvarius.com>";

const ContactSchema = z.object({
  company: z.string().max(200).optional().nullable(),
  name: z.string().min(1).max(120),
  email: z.string().email().max(200),
  topic: z.string().max(120).optional().nullable(),
  message: z.string().min(1).max(5000),
});

export type ContactInput = z.infer<typeof ContactSchema>;

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatField(label: string, value?: string | null) {
  const safeValue = value?.trim() ? escapeHtml(value.trim()) : "—";
  return `<p><strong>${label}:</strong><br />${safeValue.replace(/\n/g, "<br />")}</p>`;
}

export const submitContact = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => ContactSchema.parse(data))
  .handler(async ({ data }) => {
    const req = getRequest();
    const ip =
      req?.headers.get("cf-connecting-ip") ??
      req?.headers.get("x-forwarded-for")?.split(",")[0]?.trim() ??
      null;
    const userAgent = req?.headers.get("user-agent") ?? null;

    const { data: submission, error } = await supabaseAdmin.from("contact_submissions").insert({
      company: data.company ?? null,
      name: data.name,
      email: data.email,
      topic: data.topic ?? null,
      message: data.message,
      ip,
      user_agent: userAgent,
    }).select("id").single();

    if (error) {
      console.error("[contact] insert failed", error);
      throw new Error("Speichern fehlgeschlagen. Bitte erneut versuchen.");
    }

    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) {
      console.error("[contact] email failed: missing LOVABLE_API_KEY");
      throw new Error("E-Mail-Versand nicht konfiguriert. Bitte direkt an info@calvarius.com schreiben.");
    }

    const subject = `Neue Kontaktanfrage von ${data.name}`;
    const html = `
      <div style="font-family: Arial, sans-serif; color: #0F1419; line-height: 1.55; max-width: 640px;">
        <h1 style="font-size: 22px; margin: 0 0 18px;">Neue Kontaktanfrage</h1>
        ${formatField("Name", data.name)}
        ${formatField("E-Mail", data.email)}
        ${formatField("Unternehmen", data.company)}
        ${formatField("Thema", data.topic)}
        ${formatField("Nachricht", data.message)}
      </div>
    `;
    const text = [
      "Neue Kontaktanfrage",
      `Name: ${data.name}`,
      `E-Mail: ${data.email}`,
      `Unternehmen: ${data.company || "—"}`,
      `Thema: ${data.topic || "—"}`,
      "",
      data.message,
    ].join("\n");

    try {
      await sendLovableEmail(
        {
          to: RECIPIENT_EMAIL,
          from: FROM_EMAIL,
          sender_domain: SENDER_DOMAIN,
          reply_to: data.email,
          subject,
          html,
          text,
          purpose: "transactional",
          unsubscribe_token: crypto.randomUUID(),
          idempotency_key: `contact-${submission?.id ?? crypto.randomUUID()}`,
        },
        { apiKey },
      );
    } catch (emailError) {
      console.error("[contact] email failed", emailError);
      throw new Error("E-Mail-Versand fehlgeschlagen. Bitte direkt an info@calvarius.com schreiben.");
    }

    return { ok: true as const };
  });
